import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Key as KeyIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import CloudInitConfigForm from '../components/CloudInitConfigForm';

const CreateVMPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [osImages, setOsImages] = useState([]);
  const [goldenImages, setGoldenImages] = useState([]);
  const [imageSourceTab, setImageSourceTab] = useState(0); // 0: OS Image, 1: Golden Image
  const [selectedGoldenImageId, setSelectedGoldenImageId] = useState('');
  const [availableGPUs, setAvailableGPUs] = useState([]);
  const [cloudInitIsos, setCloudInitIsos] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [hostSSHKey, setHostSSHKey] = useState(null);
  const [isoUsageInfo, setIsoUsageInfo] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [loadingIsoInfo, setLoadingIsoInfo] = useState(false);
  const [hardwarePresets, setHardwarePresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [userdataTemplates, setUserdataTemplates] = useState([]);
  const [selectedUserdataTemplate, setSelectedUserdataTemplate] = useState('');
  const [homeDirectoriesPath, setHomeDirectoriesPath] = useState('');

  // Cloud-init 전용 설정 상태
  const [cloudInitConfig, setCloudInitConfig] = useState({
    hostname: '',
    username: 'inugpu',
    password: 'gpuinu!!',
    ssh_authorized_keys: [],
    timezone: 'Asia/Seoul',
    locale: 'ko_KR.UTF-8',
    packages: [],
    runcmd: [],
    enable_ssh: true,
    ssh_password_auth: true,
    ssh_key_auth: true,
    ssh_root_login: false,
    ssh_port: 22
  });
  const [formValues, setFormValues] = useState({
    name: '',
    memory: 4096,
    vcpus: 2,
    cpu_mode: 'host-model',  // CPU 모드: host-model, host-passthrough, custom
    os_image: '',
    disk_size: 20,
    gpu_id: '',
    network_profile: 'default',
    cloud_init: true,
    cloud_init_iso: '',
    enable_console_log: true,
    console_log_append: false,
    host_mounts: []  // 호스트 디렉토리 마운트 목록
  });

  // 데이터 로드
  useEffect(() => {
    const fetchData = async () => {
      try {
        // OS 이미지 목록 가져오기
        const imagesResponse = await apiService.getOSImages();
        setOsImages(imagesResponse.data.images || []);

        // Golden Image 목록 가져오기
        try {
          const goldenResponse = await apiService.getGoldenImages();
          setGoldenImages(goldenResponse.data.golden_images || []);
        } catch (err) {
          // Golden Image는 없어도 무방
        }

        // 전체 GPU 목록 가져오기 (할당된 GPU 포함, 태그 포함)
        const gpusResponse = await apiService.getGPUsWithTags();
        setAvailableGPUs(gpusResponse.data.gpus || []);
        
        // Cloud-init ISO 목록 가져오기
        const isosResponse = await apiService.getCloudInitIsos();
        const isos = isosResponse.data.isos || [];
        setCloudInitIsos(isos);

        // 각 ISO의 사용 정보 확인
        const usageInfo = {};
        for (const iso of isos) {
          try {
            const usageResponse = await apiService.checkCloudInitIsoInUse(iso.name);
            usageInfo[iso.name] = usageResponse.data;
          } catch (err) {
            usageInfo[iso.name] = { in_use: false, using_vms: [] };
          }
        }
        setIsoUsageInfo(usageInfo);
        
        // 네트워크 목록 가져오기
        const networksResponse = await apiService.getNetworks();
        setNetworks(networksResponse.data.networks || []);
        
        // 하드웨어 프리셋 목록 가져오기
        const presetsResponse = await apiService.getHardwarePresets();
        const presets = presetsResponse.data.presets || [];
        setHardwarePresets(presets);

        // User Data 템플릿 목록 가져오기
        const templatesResponse = await apiService.getUserDataTemplates();
        setUserdataTemplates(templatesResponse.data.templates || []);

        // 홈 디렉토리 기본 경로 가져오기
        try {
          const storagePathsResponse = await apiService.getStoragePaths();
          setHomeDirectoriesPath(storagePathsResponse.data.home_directories_path || '');
        } catch (error) {
          console.error('홈 디렉토리 경로 로드 실패:', error);
        }

        // 기본 프리셋이 있으면 자동 선택
        const defaultPreset = presets.find(p => p.is_default);
        if (defaultPreset) {
          setSelectedPreset(defaultPreset.id.toString());
          setFormValues(prev => ({
            ...prev,
            memory: defaultPreset.memory,
            vcpus: defaultPreset.vcpus,
            disk_size: defaultPreset.disk_size,
            gpu_id: defaultPreset.gpu_id || '',
            network_profile: defaultPreset.network_profile
          }));
        }
        
        // 호스트 SSH 키 가져오기
        try {
          const hostKeyResponse = await apiService.getHostSSHKey();
          if (hostKeyResponse.data.status === 'exists') {
            setHostSSHKey(hostKeyResponse.data);
          }
        } catch (error) {
          // 호스트 SSH 키는 선택사항이므로 오류 무시
        }
      } catch (error) {
        setNotification({
          open: true,
          message: '데이터를 불러오는데 실패했습니다.',
          severity: 'error'
        });
      }
    };

    fetchData();
  }, []);

  // 네비게이션 state에서 golden_image_id 처리 (GoldenImagesPage에서 이동한 경우)
  useEffect(() => {
    if (location.state?.golden_image_id) {
      setImageSourceTab(1);
      setSelectedGoldenImageId(location.state.golden_image_id);
    }
  }, [location.state]);

  // VM 이름이 변경될 때 Cloud-init 호스트명도 업데이트 (ISO가 선택되지 않은 경우에만)
  useEffect(() => {
    if (formValues.name && formValues.name !== cloudInitConfig.hostname && !formValues.cloud_init_iso) {
      setCloudInitConfig(prev => ({
        ...prev,
        hostname: formValues.name
      }));
    }
  }, [formValues.name, formValues.cloud_init_iso, cloudInitConfig.hostname]);

  // 폼 입력값 변경 처리
  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Cloud-init ISO 선택 시 해당 ISO의 설정 정보 로드
    if (name === 'cloud_init_iso' && value) {
      setLoadingIsoInfo(true);
      try {
        const response = await apiService.getCloudInitIsoInfo(value);
        
        if (response.data.status === 'success' && response.data.iso_info.user_data) {
          const userData = response.data.iso_info.user_data;

          setCloudInitConfig(prev => ({
            ...prev,
            // VM 이름이 이미 설정된 경우 호스트명 유지, 아니면 ISO의 호스트명 사용
            hostname: formValues.name || userData.hostname || prev.hostname,
            username: userData.username || prev.username,
            // 비밀번호는 보안상 복구 불가하므로 기존 값 유지
            ssh_authorized_keys: Array.isArray(userData.ssh_authorized_keys) ? userData.ssh_authorized_keys : (prev.ssh_authorized_keys || []),
            timezone: userData.timezone || prev.timezone,
            locale: userData.locale || prev.locale,
            packages: Array.isArray(userData.packages) ? userData.packages : (prev.packages || []),
            runcmd: Array.isArray(userData.runcmd) ? userData.runcmd : (prev.runcmd || []),
            enable_ssh: userData.enable_ssh !== undefined ? userData.enable_ssh : prev.enable_ssh,
            ssh_password_auth: userData.ssh_password_auth !== undefined ? userData.ssh_password_auth : prev.ssh_password_auth,
            ssh_key_auth: userData.ssh_key_auth !== undefined ? userData.ssh_key_auth : prev.ssh_key_auth,
            ssh_root_login: userData.ssh_root_login !== undefined ? userData.ssh_root_login : prev.ssh_root_login,
            ssh_port: userData.ssh_port || prev.ssh_port
          }));

          setNotification({
            open: true,
            message: `Cloud-init ISO "${value}"의 설정을 불러왔습니다. (SSH 키: ${userData.ssh_authorized_keys?.length || 0}개, 패키지: ${userData.packages?.length || 0}개, 명령: ${userData.runcmd?.length || 0}개)`,
            severity: 'success'
          });
        } else {
          setNotification({
            open: true,
            message: `ISO 정보를 불러왔으나 user_data가 비어있습니다. 기본값을 사용합니다.`,
            severity: 'warning'
          });
        }
      } catch (error) {
        setNotification({
          open: true,
          message: 'ISO 설정 정보를 불러오는데 실패했습니다.',
          severity: 'warning'
        });
      } finally {
        setLoadingIsoInfo(false);
      }
    } else if (name === 'cloud_init_iso' && !value) {
      // ISO 선택 해제 시 기본값으로 리셋 (VM 이름은 유지)
      setCloudInitConfig(prev => ({
        hostname: formValues.name || '',
        username: 'inugpu',
        password: 'gpuinu!!',
        ssh_authorized_keys: [],
        timezone: 'Asia/Seoul',
        locale: 'ko_KR.UTF-8',
        packages: [],
        runcmd: [],
        enable_ssh: true,
        ssh_password_auth: true,
        ssh_key_auth: true,
        ssh_root_login: false,
        ssh_port: 22
      }));
    }
  };

  // 하드웨어 프리셋 선택 처리
  const handlePresetChange = (e) => {
    const presetId = e.target.value;
    setSelectedPreset(presetId);

    if (presetId) {
      const preset = hardwarePresets.find(p => p.id.toString() === presetId);
      if (preset) {
        setFormValues(prev => ({
          ...prev,
          memory: preset.memory,
          vcpus: preset.vcpus,
          disk_size: preset.disk_size,
          gpu_id: preset.gpu_id || '',
          network_profile: preset.network_profile
        }));

        setNotification({
          open: true,
          message: `VM 프리셋 "${preset.name}"이 적용되었습니다.`,
          severity: 'success'
        });
      }
    }
  };

  // User Data 템플릿 선택 처리
  const handleUserdataTemplateChange = (e) => {
    const templateName = e.target.value;
    setSelectedUserdataTemplate(templateName);

    if (templateName) {
      const template = userdataTemplates.find(t => t.name === templateName);
      if (template) {
        // 템플릿의 packages와 runcmd를 현재 설정과 병합
        const currentPackages = cloudInitConfig.packages || [];
        const templatePackages = template.packages || [];
        const mergedPackages = [...new Set([...templatePackages, ...currentPackages])]; // 중복 제거

        const currentRuncmd = cloudInitConfig.runcmd || [];
        const templateRuncmd = template.runcmd || [];
        const mergedRuncmd = [...templateRuncmd, ...currentRuncmd]; // 템플릿 명령을 먼저 실행

        setCloudInitConfig(prev => ({
          ...prev,
          packages: mergedPackages,
          runcmd: mergedRuncmd
        }));

        setNotification({
          open: true,
          message: `User Data 템플릿 "${template.name}"이 적용되었습니다. (패키지: ${templatePackages.length}개, 명령: ${templateRuncmd.length}개)`,
          severity: 'success'
        });
      }
    }
  };

  // 호스트 마운트 추가
  const handleAddHostMount = () => {
    setFormValues(prev => ({
      ...prev,
      host_mounts: [
        ...prev.host_mounts,
        { host_path: '', mount_tag: '', read_only: false }
      ]
    }));
  };

  // 홈 디렉토리 자동 추가
  const handleAddHomeDirectory = () => {
    const username = cloudInitConfig.username || 'user';
    const vmName = formValues.name || 'vm';

    if (!homeDirectoriesPath) {
      setNotification({
        open: true,
        message: '홈 디렉토리 기본 경로가 설정되지 않았습니다. 스토리지 설정에서 경로를 설정해주세요.',
        severity: 'warning'
      });
      return;
    }

    const homePath = `${homeDirectoriesPath}/${username}`;

    setFormValues(prev => ({
      ...prev,
      host_mounts: [
        ...prev.host_mounts,
        { host_path: homePath, mount_tag: 'home', read_only: false }
      ]
    }));

    setNotification({
      open: true,
      message: `홈 디렉토리가 추가되었습니다: ${homePath}`,
      severity: 'success'
    });
  };

  // 호스트 마운트 제거
  const handleRemoveHostMount = (index) => {
    setFormValues(prev => ({
      ...prev,
      host_mounts: prev.host_mounts.filter((_, i) => i !== index)
    }));
  };

  // 호스트 마운트 수정
  const handleHostMountChange = (index, field, value) => {
    setFormValues(prev => ({
      ...prev,
      host_mounts: prev.host_mounts.map((mount, i) =>
        i === index ? { ...mount, [field]: value } : mount
      )
    }));
  };

  // 폼 제출 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Cloud-init 설정과 기본 VM 데이터를 결합
      const vmData = {
        ...formValues,
        // 이미지 소스: Golden Image 탭이면 golden_image_id, OS 이미지 탭이면 os_image
        os_image: imageSourceTab === 0 ? formValues.os_image : null,
        golden_image_id: imageSourceTab === 1 ? selectedGoldenImageId : null,
        // Cloud-init 설정 병합
        username: cloudInitConfig.username,
        password: cloudInitConfig.password,
        ssh_key: cloudInitConfig.ssh_authorized_keys.join('\n'), // 이전 방식 지원
        ssh_authorized_keys: cloudInitConfig.ssh_authorized_keys, // 새로운 배열 방식
        timezone: cloudInitConfig.timezone,
        locale: cloudInitConfig.locale,
        packages: cloudInitConfig.packages,
        runcmd: cloudInitConfig.runcmd,
        enable_ssh: cloudInitConfig.enable_ssh,
        ssh_password_auth: cloudInitConfig.ssh_password_auth,
        ssh_key_auth: cloudInitConfig.ssh_key_auth,
        ssh_root_login: cloudInitConfig.ssh_root_login,
        ssh_port: cloudInitConfig.ssh_port,
        userdata_template: selectedUserdataTemplate || null
      };

      await apiService.createVM(vmData);

      // 선택된 프리셋에 포트포워딩 규칙이 있으면 자동 적용 시도
      if (selectedPreset) {
        const preset = hardwarePresets.find(p => p.id.toString() === selectedPreset);
        const pfRules = preset?.port_forwarding_rules || [];
        if (pfRules.length > 0) {
          try {
            // VM이 IP를 얻을 시간을 잠시 대기
            await new Promise(resolve => setTimeout(resolve, 3000));
            const pfResult = await apiService.applyPresetPortForwards(parseInt(selectedPreset), formValues.name);
            const { applied, failed } = pfResult.data;
            if (applied && applied.length > 0) {
              setNotification({
                open: true,
                message: `VM ${formValues.name} 생성 완료. 포트 포워딩 ${applied.length}개 규칙 적용됨.${failed?.length > 0 ? ` (${failed.length}개 실패 - VM 실행 후 수동 적용 필요)` : ''}`,
                severity: applied.length > 0 ? 'success' : 'warning'
              });
            } else {
              setNotification({
                open: true,
                message: `VM ${formValues.name} 생성 완료. 포트 포워딩은 VM 실행 후 포트 포워딩 페이지에서 수동으로 적용하세요.`,
                severity: 'warning'
              });
            }
          } catch {
            setNotification({
              open: true,
              message: `VM ${formValues.name} 생성 완료. 포트 포워딩 자동 적용 실패 - VM 실행 후 수동 적용이 필요합니다.`,
              severity: 'warning'
            });
          }
        } else {
          setNotification({ open: true, message: `VM ${formValues.name} 생성에 성공했습니다.`, severity: 'success' });
        }
      } else {
        setNotification({ open: true, message: `VM ${formValues.name} 생성에 성공했습니다.`, severity: 'success' });
      }

      // VM 목록 페이지로 이동
      setTimeout(() => {
        navigate('/vms');
      }, 2000);
    } catch (error) {
      setNotification({
        open: true,
        message: `VM 생성 실패: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // 알림 닫기 처리
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };
  
  // Cloud-init 설정 변경 처리
  const handleCloudInitConfigChange = (e) => {
    const { name, value, checked } = e.target;
    setCloudInitConfig(prev => ({
      ...prev,
      [name]: name.includes('ssh_') && name !== 'ssh_port' ? checked : value
    }));
  };

  // SSH 키 입력 변경 처리
  const handleCloudInitSshKeysChange = (e) => {
    const keys = e.target.value.split('\n').filter(key => key.trim());
    setCloudInitConfig(prev => ({
      ...prev,
      ssh_authorized_keys: keys
    }));
  };

  // 패키지 입력 변경 처리
  const handleCloudInitPackagesChange = (e) => {
    const packages = e.target.value.split('\n').filter(pkg => pkg.trim());
    setCloudInitConfig(prev => ({
      ...prev,
      packages: packages
    }));
  };

  // 실행 명령 입력 변경 처리
  const handleCloudInitRuncmdChange = (e) => {
    const commands = e.target.value.split('\n').filter(cmd => cmd.trim());
    setCloudInitConfig(prev => ({
      ...prev,
      runcmd: commands
    }));
  };

  // 호스트 SSH 키 로드
  const loadHostSSHKey = () => {
    if (hostSSHKey && hostSSHKey.public_key) {
      const currentKeys = Array.isArray(cloudInitConfig.ssh_authorized_keys) 
        ? cloudInitConfig.ssh_authorized_keys 
        : cloudInitConfig.ssh_authorized_keys ? [cloudInitConfig.ssh_authorized_keys] : [];
      
      // 중복 키 체크
      if (!currentKeys.includes(hostSSHKey.public_key)) {
        setCloudInitConfig(prev => ({
          ...prev,
          ssh_authorized_keys: [...currentKeys, hostSSHKey.public_key]
        }));
        setNotification({
          open: true,
          message: '호스트 SSH 키가 추가되었습니다.',
          severity: 'success'
        });
      } else {
        setNotification({
          open: true,
          message: '호스트 SSH 키가 이미 추가되어 있습니다.',
          severity: 'info'
        });
      }
    } else {
      setNotification({
        open: true,
        message: '호스트 SSH 키를 찾을 수 없습니다.',
        severity: 'warning'
      });
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        새 VM 생성
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* 기본 정보 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                기본 정보
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="VM 이름"
                name="name"
                value={formValues.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Tabs
                  value={imageSourceTab}
                  onChange={(_, v) => setImageSourceTab(v)}
                  sx={{ mb: 1 }}
                >
                  <Tab label="OS 이미지" />
                  <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><StarIcon fontSize="small" color="warning" />Golden Image</Box>} />
                </Tabs>
                {imageSourceTab === 0 && (
                  <TextField
                    required
                    fullWidth
                    select
                    label="OS 이미지"
                    name="os_image"
                    value={formValues.os_image}
                    onChange={handleChange}
                  >
                    {osImages.map((image) => (
                      <MenuItem key={image.name} value={image.name}>
                        {image.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {imageSourceTab === 1 && (
                  <TextField
                    required
                    fullWidth
                    select
                    label="Golden Image"
                    value={selectedGoldenImageId}
                    onChange={e => setSelectedGoldenImageId(e.target.value)}
                  >
                    {goldenImages.length === 0 ? (
                      <MenuItem value="" disabled>등록된 Golden Image가 없습니다</MenuItem>
                    ) : (
                      goldenImages.map((img) => (
                        <MenuItem key={img.id} value={img.id}>
                          {img.display_name}
                          {img.tags?.length > 0 && ` [${img.tags.join(', ')}]`}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                )}
              </Box>
            </Grid>
            
            {/* 하드웨어 설정 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                하드웨어 설정
              </Typography>
            </Grid>
            
            {/* VM 프리셋 선택 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="VM 프리셋 (선택)"
                value={selectedPreset}
                onChange={handlePresetChange}
                helperText="프리셋을 선택하면 하드웨어 설정이 자동으로 적용됩니다"
              >
                <MenuItem value="">
                  <em>직접 설정</em>
                </MenuItem>
                {hardwarePresets.map((preset) => (
                  <MenuItem key={preset.id} value={preset.id.toString()}>
                    {preset.name}
                    {preset.is_default && ' (기본)'}
                    {' - '}
                    {(preset.memory / 1024).toFixed(1)}GB RAM, {preset.vcpus}vCPU, {preset.disk_size}GB
                    {(preset.port_forwarding_rules || []).length > 0 && ` | 포트포워딩 ${preset.port_forwarding_rules.length}개`}
                  </MenuItem>
                ))}
              </TextField>
              {selectedPreset && (() => {
                const preset = hardwarePresets.find(p => p.id.toString() === selectedPreset);
                const pfRules = preset?.port_forwarding_rules || [];
                if (pfRules.length === 0) return null;
                return (
                  <Alert severity="info" sx={{ mt: 1 }} icon={false}>
                    이 프리셋에는 포트 포워딩 규칙 {pfRules.length}개가 포함되어 있습니다.
                    VM 생성 후 자동으로 적용을 시도합니다 (VM이 실행 중이고 IP가 있어야 함):&nbsp;
                    {pfRules.map((r, i) => (
                      <strong key={i}>{i > 0 ? ', ' : ''}{r.protocol.toUpperCase()}:{r.vm_port}{r.description ? `(${r.description})` : ''}</strong>
                    ))}
                  </Alert>
                );
              })()}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                type="number"
                label="메모리 (MB)"
                name="memory"
                value={formValues.memory}
                onChange={handleChange}
                inputProps={{ min: 1024, step: 1024 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                type="number"
                label="vCPU 수"
                name="vcpus"
                value={formValues.vcpus}
                onChange={handleChange}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="CPU 모드"
                name="cpu_mode"
                value={formValues.cpu_mode}
                onChange={handleChange}
                helperText="host-model: 호스트 CPU 기능 대부분 사용 (권장), host-passthrough: 호스트 CPU 완전 노출 (마이그레이션 제한)"
              >
                <MenuItem value="host-model">Host Model (권장)</MenuItem>
                <MenuItem value="host-passthrough">Host Passthrough</MenuItem>
                <MenuItem value="custom">Custom (기본)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              {(() => {
                const selectedGolden = imageSourceTab === 1
                  ? goldenImages.find(g => g.id === selectedGoldenImageId)
                  : null;
                // size는 파일 크기(bytes)이므로 가상 디스크 크기와 다를 수 있으나 최소 하한으로 활용
                const minFromGolden = selectedGolden
                  ? Math.ceil(selectedGolden.size / (1024 ** 3))
                  : 10;
                const minDisk = Math.max(10, minFromGolden);
                const tooSmall = imageSourceTab === 1 && selectedGolden && formValues.disk_size < minFromGolden;
                return (
                  <TextField
                    required
                    fullWidth
                    type="number"
                    label="디스크 크기 (GB)"
                    name="disk_size"
                    value={formValues.disk_size}
                    onChange={handleChange}
                    inputProps={{ min: minDisk }}
                    error={tooSmall}
                    helperText={
                      selectedGolden
                        ? tooSmall
                          ? `⚠️ Golden Image 파일 크기(${minFromGolden}GB 이상)보다 작습니다.`
                          : `Golden Image 기준 최소 ${minFromGolden}GB`
                        : ''
                    }
                    FormHelperTextProps={{ sx: { color: tooSmall ? 'error.main' : 'text.secondary' } }}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="GPU 할당 (선택)"
                name="gpu_id"
                value={formValues.gpu_id}
                onChange={handleChange}
                helperText={
                  formValues.gpu_id &&
                  availableGPUs.find(g => g.pci_id === formValues.gpu_id)?.assigned
                    ? `⚠️ 이 GPU는 현재 "${availableGPUs.find(g => g.pci_id === formValues.gpu_id)?.vm_name}"에 할당되어 있습니다. VM 생성 시 해당 VM에서 해제됩니다.`
                    : ''
                }
                FormHelperTextProps={{ sx: { color: 'warning.main' } }}
              >
                <MenuItem value="">
                  <em>할당 안함</em>
                </MenuItem>
                {availableGPUs.map((gpu) => (
                  <MenuItem key={gpu.pci_id} value={gpu.pci_id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span>
                        {gpu.tag ? `[${gpu.tag}] ` : ''}{gpu.model || gpu.name} ({gpu.pci_id})
                      </span>
                      {gpu.assigned && (
                        <Chip
                          label={`할당됨: ${gpu.vm_name}`}
                          size="small"
                          color="warning"
                          sx={{ ml: 'auto' }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="네트워크 프로필"
                name="network_profile"
                value={formValues.network_profile}
                onChange={handleChange}
                helperText="VM이 연결될 네트워크를 선택하세요"
              >
                {networks.map((network) => (
                  <MenuItem key={network.name} value={network.name}>
                    {network.name} ({network.cidr})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            {/* Cloud-init 설정 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Cloud-init 설정
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.cloud_init}
                    onChange={handleChange}
                    name="cloud_init"
                  />
                }
                label="Cloud-init 사용"
              />
            </Grid>
            
            {formValues.cloud_init && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="기존 Cloud-init ISO 사용 (선택)"
                    name="cloud_init_iso"
                    value={formValues.cloud_init_iso}
                    onChange={handleChange}
                    disabled={loadingIsoInfo}
                    helperText={loadingIsoInfo ? "ISO 설정 정보를 불러오는 중..." : "기존 ISO를 선택하면 해당 설정을 기반으로 하되 아래 설정으로 오버라이드됩니다"}
                  >
                    <MenuItem value="">
                      <em>새로 생성</em>
                    </MenuItem>
                    {cloudInitIsos.map((iso) => {
                      const usage = isoUsageInfo[iso.name];
                      const usageText = usage && usage.in_use
                        ? ` - 사용중: ${usage.using_vms.map(vm => vm.name).join(', ')}`
                        : '';

                      return (
                        <MenuItem key={iso.name} value={iso.name}>
                          {iso.name} ({iso.hostname}){usageText}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="User Data 템플릿 (선택)"
                    value={selectedUserdataTemplate}
                    onChange={handleUserdataTemplateChange}
                    helperText="템플릿을 선택하면 패키지와 실행 명령이 자동으로 추가됩니다"
                  >
                    <MenuItem value="">
                      <em>템플릿 없음</em>
                    </MenuItem>
                    {userdataTemplates.map((template) => (
                      <MenuItem key={template.name} value={template.name}>
                        {template.name}
                        {template.description && ` - ${template.description}`}
                        {` (패키지: ${template.packages?.length || 0}개, 명령: ${template.runcmd?.length || 0}개)`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  
                  {/* 선택된 ISO 정보 미리보기 */}
                  {formValues.cloud_init_iso && !loadingIsoInfo && (
                    <Box sx={{ mt: 1, p: 2, bgcolor: 'info.light', borderRadius: 1, border: 1, borderColor: 'info.main' }}>
                      <Typography variant="body2" color="info.dark" gutterBottom>
                        📋 선택된 ISO 설정 미리보기:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip size="small" label={`사용자: ${cloudInitConfig.username}`} />
                        <Chip size="small" label={`SSH 포트: ${cloudInitConfig.ssh_port}`} />
                        <Chip size="small" label={`SSH 키: ${cloudInitConfig.ssh_authorized_keys.length}개`} />
                        {cloudInitConfig.packages.length > 0 && (
                          <Chip size="small" label={`패키지: ${cloudInitConfig.packages.length}개`} />
                        )}
                        {cloudInitConfig.runcmd.length > 0 && (
                          <Chip size="small" label={`실행 명령: ${cloudInitConfig.runcmd.length}개`} />
                        )}
                      </Box>
                      <Typography variant="caption" color="info.dark" sx={{ mt: 1, display: 'block' }}>
                        아래 설정 항목으로 이 정보들을 수정할 수 있습니다.
                      </Typography>
                    </Box>
                  )}
                  
                  {loadingIsoInfo && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        ISO 설정 정보를 불러오는 중...
                      </Typography>
                    </Box>
                  )}
                </Grid>
                
                {/* Cloud-init 상세 설정 */}
                <CloudInitConfigForm
                  config={cloudInitConfig}
                  onConfigChange={handleCloudInitConfigChange}
                  onSshKeysChange={handleCloudInitSshKeysChange}
                  onPackagesChange={handleCloudInitPackagesChange}
                  onRuncmdChange={handleCloudInitRuncmdChange}
                  hostSSHKey={hostSSHKey}
                  onLoadHostSSHKey={loadHostSSHKey}
                  showAdvanced={false}
                  helperTextSuffix={formValues.cloud_init_iso ? " (기존 ISO 설정을 오버라이드)" : ""}
                />
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    💡 고급 설정 (패키지 설치, 실행 명령 등)은 Cloud-init 관리 페이지에서 별도로 관리할 수 있습니다.
                  </Typography>
                </Grid>
              </>
            )}

            {/* 호스트 디렉토리 마운트 설정 */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  호스트 디렉토리 마운트
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<FolderIcon />}
                    onClick={handleAddHomeDirectory}
                    size="small"
                    sx={{ mr: 1 }}
                    disabled={!homeDirectoriesPath}
                  >
                    홈 디렉토리 추가
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddHostMount}
                    size="small"
                  >
                    마운트 추가
                  </Button>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                호스트의 디렉토리를 VM에 마운트할 수 있습니다. 마운트된 디렉토리는 VM 내에서 9p 파일시스템으로 사용됩니다.
                {homeDirectoriesPath && (
                  <span>
                    {' '}홈 디렉토리 기본 경로: <strong>{homeDirectoriesPath}</strong>
                  </span>
                )}
              </Typography>
            </Grid>

            {formValues.host_mounts.map((mount, index) => (
              <Grid item xs={12} key={index}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="호스트 경로"
                        value={mount.host_path}
                        onChange={(e) => handleHostMountChange(index, 'host_path', e.target.value)}
                        placeholder="/path/on/host"
                        helperText="호스트의 디렉토리 절대 경로 (예: /home/user/shared)"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FolderIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="마운트 태그"
                        value={mount.mount_tag}
                        onChange={(e) => handleHostMountChange(index, 'mount_tag', e.target.value)}
                        placeholder="shared"
                        helperText="VM 내에서 사용할 태그 (예: shared, data, workspace)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={mount.read_only}
                            onChange={(e) => handleHostMountChange(index, 'read_only', e.target.checked)}
                          />
                        }
                        label="읽기 전용"
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Tooltip title="제거">
                        <IconButton
                          onClick={() => handleRemoveHostMount(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}

            {formValues.host_mounts.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2" gutterBottom>
                    <strong>VM 내에서 마운트하는 방법:</strong>
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', mt: 1 }}>
                    {formValues.host_mounts.map((mount, index) => (
                      mount.mount_tag && (
                        <div key={index}>
                          sudo mount -t 9p -o trans=virtio {mount.mount_tag} /mnt/{mount.mount_tag}
                        </div>
                      )
                    ))}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    또는 /etc/fstab에 추가하여 자동 마운트 설정:
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', mt: 1 }}>
                    {formValues.host_mounts.map((mount, index) => (
                      mount.mount_tag && (
                        <div key={index}>
                          {mount.mount_tag} /mnt/{mount.mount_tag} 9p trans=virtio,version=9p2000.L{mount.read_only ? ',ro' : ''} 0 0
                        </div>
                      )
                    ))}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* 콘솔 로깅 설정 */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                콘솔 로깅 설정
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.enable_console_log}
                    onChange={handleChange}
                    name="enable_console_log"
                  />
                }
                label="시리얼 콘솔 로그 활성화"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                VM의 시리얼 콘솔 출력을 파일로 저장합니다. VMConsoleViewer에서 로그를 확인할 수 있습니다.
              </Typography>
            </Grid>
            {formValues.enable_console_log && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.console_log_append}
                      onChange={handleChange}
                      name="console_log_append"
                    />
                  }
                  label="로그 파일에 추가 모드 사용"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  활성화하면 VM 재시작 시 기존 로그에 이어서 기록합니다. 비활성화하면 기존 로그를 덮어씁니다.
                </Typography>
              </Grid>
            )}

            {/* 제출 버튼 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {loading ? '생성 중...' : 'VM 생성'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {/* 알림 메시지 */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateVMPage; 